import imageCompression from 'browser-image-compression';
import capitalize from 'capitalize';
import _ from 'lodash';
import moment from 'moment';
import numeral from 'numeral';
import { toast } from 'sonner';
import { agentLandingPageSampleTokens, EAgentLandingPageType } from 'types/agent-landing-page';
import { ECampaignStatus } from 'types/campaign';
import { EEventType, IEventType } from 'types/event-type';
import { ETouchTriggerNotSentReason } from 'types/touch-trigger';
import { ETouchType } from 'types/touch-type';
import XRegExp from 'xregexp';

import ContactMailIcon from '@material-ui/icons/ContactMail';
import DescriptionIcon from '@material-ui/icons/Description';
import EmailIcon from '@material-ui/icons/Email';
import SmsIcon from '@material-ui/icons/Sms';
import VoicemailIcon from '@material-ui/icons/Voicemail';

const intercomAppId = 'ykyzek5d';

const supportEmail = 'support@harvist.com';

// Use XRegExp to match all unicode letters (like accented ones too)
const nameRegExp = XRegExp(/^[\p{L}\p{N}&+\-. ]+$/u); // Allow letters, numbers, spaces, '&', '-', '.', and '+'
const nameRegExpGeneral = XRegExp(/^[\p{L}0-9&\-() ]+$/u); // Allows way more characters than normal (used for imported event data names usually)
const phoneRegExp = /^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/;
const birthdayRegExp = /^(0[1-9]|1[012])\/(0[1-9]|[12][0-9]|3[01])\/(19|20)\d\d$/;
const birthdayNoYearRegExp = /^(0[1-9]|1[012])\/(0[1-9]|[12][0-9]|3[01])$/;
const urlRegExp = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,10}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/;
const httpsRegex = /^https?:\/\//;

// >= 3K APNs across the brokerage or an individual agent account
// const totalNumberOfApnsBeingFarmedForDiscount = 3000;

// NOTE: numApns must be in descending order!
const monthlyRatesPerApn = [
  { numApns: 3000, rate: 110 },
  { numApns: 2000, rate: 125 },
  { numApns: 1000, rate: 150 },
  { numApns: 500, rate: 175 },
  { numApns: 400, rate: 200 },
  { numApns: 300, rate: 225 },
  { numApns: 200, rate: 250 },
  { numApns: 150, rate: 266 },
  { numApns: 100, rate: 300 }, // Used for 0-99 rate too
];

const nodMonthlyRatePerApn = 500; // $5.00

// 13400 Ventura Blvd, Sherman Oaks, CA 91423
const corporateOfficeLocation = {
  lat: 34.1467852,
  lng: -118.4250829,
};

const supportPhoneNumber = '18183019800';
const supportPhoneNumberFormatted = '+1 (818) 301-9800';

export const agentAdvicePageUrl = 'a8413098-3cbf-448b-97e9-0ac3cea99a34';

export const minPasswordLength = 8;
export const maxPasswordLength = 16;
export const passwordComplexityOptions = {
  min: 8,
  max: 16,
  lowerCase: 1,
  upperCase: 1,
  numeric: 1,
  symbol: 1,
  requirementCount: 4,
};
export const passwordValidationChecks = [
  {
    description: 'At least 8 characters',
    errorName: 'passwordComplexity.tooShort',
  },
  {
    description: 'One lowercase character',
    errorName: 'passwordComplexity.lowercase',
  },
  {
    description: 'One uppercase character',
    errorName: 'passwordComplexity.uppercase',
  },
  {
    description: 'One number',
    errorName: 'passwordComplexity.numeric',
  },
  {
    description: 'One symbol',
    errorName: 'passwordComplexity.symbol',
  },
];

export const isAgentLandingPageSample = (token: string) => Object.values(agentLandingPageSampleTokens).includes(token);

export const getAgentLandingPageSampleUrlFromType = (agentLandingPageType: EAgentLandingPageType, eventId?: string) => {
  const token = agentLandingPageSampleTokens[agentLandingPageType];

  let url = `${process.env.REACT_APP_AGENT_LANDING_URL}/?token=${token}`;

  if (eventId) url = `${url}&subject=${eventId}`;

  return url;
};

// Types exist on both frontend and backend
export const voicemailTouchBehaviors = {
  ALL_LEADS: 'Send my voicemail to all leads.',
  CONSENTED_LEADS: 'Send my voicemail to leads who have scanned my QR code and an email to all others.',
  NON_CONSENTED_LEADS: 'Send my voicemail to leads who have NOT scanned my QR code and an email to all others.',
  NONE: 'Don\'t send any voicemails. All of my leads will receive an email.',
};

export const enumToReadableName = (s: string) => capitalize.words(s.replaceAll('_', ' '));

const clampNumber = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const numberWithCommas = (num: number | string) => num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

// 850000 -> $850K, 1234000 -> $1.23M
const numberAsAbbreviatedPrice = (num: number) => numeral(num).format(num >= 1000000 ? '$0.00a' : '$0a').toUpperCase();

export const urlWithoutScheme = (url: string) => url.replace(httpsRegex, '');

const compressImageUpload = async (imageFile: File) => {
  try {
    // TODO: KINDA SUCKS???
    const compressedFile = await imageCompression(imageFile, { maxSizeMB: 1, maxWidthOrHeight: 2048 });

    return await imageCompression.getDataUrlFromFile(compressedFile);
  } catch (err) {
    console.error(err);
  }

  return null;
};

const isCrmProEventType = (eventType: IEventType) => eventType.name === EEventType.CRMPro;
const isNODEventType = (eventType: IEventType) => eventType.name === EEventType.NOD;
const isABSEventType = (eventType: any) => eventType.name === EEventType.AbsenteeOwners;
const isCommercialEventType = (eventType: IEventType) => eventType.name === EEventType.Commercial;
const isFullFarmEventType = (eventType: any) => eventType.name === EEventType.FullFarm;

const filterEventType = (eventTypes: IEventType[], typeChecker: (eventType: IEventType)=> boolean) => {
  const filtered = eventTypes.filter((e) => typeChecker(e));
  return filtered.length === 1 ? filtered[0] : null;
};

/**
 * Converts the event type name to a URL path
 * E.g. 'Absentee Owners' -> 'absentee-owners'
 * @param {string} eventTypeName - Event type name
 */
const eventTypeNameToUrlPath = (eventTypeName: EEventType) => eventTypeName.toLowerCase().replaceAll(' ', '-');

const stringifyAddressObject = (address: any) => {
  if (!address) return '';

  const {
    address1, address2, city, state, zipcode,
  } = address;

  return `${address1}${address2 ? ` ${address2}` : ''}, ${city}, ${state} ${zipcode}`;
};

const stringifyEventSiteAddressObject = (siteAddress: any) => {
  if (!siteAddress) return '';

  const {
    address, city, state, zipcode,
  } = siteAddress;

  return `${address}, ${city}, ${state} ${zipcode}`;
};

const formatPhoneNumber = (phoneNumberString: string, numbersOnly: boolean = false) => {
  const cleaned = (`${phoneNumberString}`).replace(/\D/g, '');
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    const intlCode = (match[1] ? '+1 ' : '');
    if (numbersOnly) return [intlCode, match[2], match[3], match[4]].join('');
    return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
  }
  return phoneNumberString;
};

/**
 * "+1 (888) 888-8888" -> "+1.888.888.8888"
 */
export function formatPhoneDotsIntl(input: string | number): string {
  const raw = String(input).trim();

  // Extract digits but keep leading + if present
  const match = raw.match(/^(\+\d+)?(.*)$/);
  const intl = match?.[1] || ''; // like +44 or +1
  const rest = (match?.[2] || '').replace(/\D/g, '');

  const area = rest.slice(0, 3);
  const mid = rest.slice(3, 6);
  const last = rest.slice(6, 10);

  let formatted = '';
  if (last) formatted = `${area}.${mid}.${last}`;
  else if (mid) formatted = `${area}.${mid}`;
  else if (area) formatted = area;

  if (intl) {
    // prepend with intl code and a dot
    return `${intl}.${formatted}`;
  }

  return formatted;
}

const isCampaignDraft = (campaignStatus: ECampaignStatus) => campaignStatus === ECampaignStatus.Draft;
const isCampaignPending = (campaignStatus: ECampaignStatus) => [ECampaignStatus.PendingDocusign, ECampaignStatus.PendingData, ECampaignStatus.PendingBilling].includes(campaignStatus);
const isCampaignActiveOrCompleted = (campaignStatus: ECampaignStatus) => [ECampaignStatus.Active, ECampaignStatus.Completed].includes(campaignStatus);
const isCampaignPaused = (campaignStatus: ECampaignStatus) => campaignStatus === ECampaignStatus.Paused;
const isCampaignCancelled = (campaignStatus: ECampaignStatus) => campaignStatus === ECampaignStatus.Cancelled;

/**
 * This gives approximate dates on either the 1st or 15th of the month. The backend
 * campaign start/end date calculations are more more complex.
 */
const calculateCampaignDates = (eventType: IEventType) => {
  const { campaignLengthInMonths } = eventType;

  // Calculate the campaign start and end date. Start date is the nearest 1st or 15th of the month,
  // with a buffer of at least some # of days.
  const minimumNumDaysBeforeCampaignStarts = 8; // Doesn't matter too much. Check comment above.
  const currentDateWithBuffer = moment().utc().add(minimumNumDaysBeforeCampaignStarts, 'days');
  let campaignStartDate;

  if (currentDateWithBuffer.date() >= 15) {
    campaignStartDate = moment(currentDateWithBuffer).utc().add(1, 'M').date(1)
      .hour(20); // ~ 12 PM PST which is 20 UTC
  } else {
    campaignStartDate = moment(currentDateWithBuffer).utc().date(15).hour(20); // ~ 12 PM PST which is 20 UTC
  }

  const campaignEndDate = moment(campaignStartDate).add(campaignLengthInMonths, 'M');

  return { campaignStartDate, campaignEndDate };
};

const campaignEventTypeCanBeBilledAnnually = (eventType: IEventType) => eventType.name === EEventType.AbsenteeOwners || eventType.name === EEventType.FullFarm;

const calculateCampaignPrices = (
  eventType: IEventType,
  numberOfApns: number,
  numberOfExtraRecipients: number,
  numApnsAlreadyBeingFarmed: number,
  customMonthlyRatePerApn: number | undefined,
  renewalPreviousMonthlyRatePerApn: number | undefined,
) => {
  let monthlyRatePerApn;

  if (eventType.name === EEventType.NOD) {
    // Different rate for NODs and no discount offered
    monthlyRatePerApn = nodMonthlyRatePerApn;
  } else if (renewalPreviousMonthlyRatePerApn && renewalPreviousMonthlyRatePerApn <= 125) {
    // Pricing is being calculated for a campaign that is being renewed.
    // If the previous campaigns rate per apn is <= $1.25, then step up
    // to the next (old) pricing tier and cap it at $1.25 per

    if (renewalPreviousMonthlyRatePerApn <= 86) {
      monthlyRatePerApn = 95;
    } else if (renewalPreviousMonthlyRatePerApn <= 95) {
      monthlyRatePerApn = 110;
    } else if (renewalPreviousMonthlyRatePerApn <= 110) {
      monthlyRatePerApn = 125;
    } else {
      monthlyRatePerApn = 125;
    }
  } else if (customMonthlyRatePerApn) {
    monthlyRatePerApn = customMonthlyRatePerApn;
  } else {
    for (let i = 0; i < monthlyRatesPerApn.length; i++) {
      const { numApns, rate } = monthlyRatesPerApn[i];
      if (numberOfApns >= numApns) {
        monthlyRatePerApn = rate;
        break;
      }
    }

    // They have less than the first threshold
    if (!monthlyRatePerApn) monthlyRatePerApn = _.last(monthlyRatesPerApn)!.rate;
  }
  // } else if (numApnsAlreadyBeingFarmed + numberOfApns >= totalNumberOfApnsBeingFarmedForDiscount || applyDiscountedAPNRate) {
  //   // Discounted rate if the brokerage or individual agent farms a minimum number of APNs
  //   monthlyRatePerApn = discountedMonthlyRatePerApn;
  // }

  const apnsMonthlyCost = Math.ceil(monthlyRatePerApn * numberOfApns);
  const extraRecipientsMonthlyCost = Math.ceil(monthlyRatePerApn * numberOfExtraRecipients);
  const totalMonthlyCost = apnsMonthlyCost + extraRecipientsMonthlyCost;

  const costs = {
    monthlyRatePerApn,
    apnsMonthlyCost,
    extraRecipientsMonthlyCost,
    totalMonthlyCost,
  } as any;

  // Calculate annual billing cost
  if (campaignEventTypeCanBeBilledAnnually(eventType)) {
    // TODO: Hardcode the "1 month free" for now
    // 1 month off for annual billing
    const annualTotal = totalMonthlyCost * (eventType.campaignLengthInMonths! - 1);

    costs.annualDiscount = totalMonthlyCost;
    costs.annualEffectiveMonthly = annualTotal / eventType.campaignLengthInMonths!;
    costs.annualTotal = annualTotal;
  }

  return costs;
};

// TODO: Why do we need this? Kinda messy. Can't we just check this on the fly and cache it when we need it or something?
const populateCampaignEventDataEventOverrideData = (campaigns: any[]) => {
  campaigns.forEach((campaign) => {
    campaign.subscribedEvents.forEach((eventData: any) => {
      const { _id, siteAddress: { zipcode } } = eventData;

      const [existingEventOverride] = campaign.eventOverrides.filter((o: any) => o.event === _id);

      if (existingEventOverride) {
        // eslint-disable-next-line no-param-reassign
        eventData.existingEventOverride = existingEventOverride;
      }
    });
  });
};

export const isCampaignUsingCalendarBasedSystem = (eventTypeName: EEventType) => ['Absentee Owners', 'Commercial', 'Full Farm'].includes(eventTypeName);

export const sortTouchesForCampaignSteps = (campaign: any) => {
  const { touchTriggers } = campaign;

  // No need to do extra sorting for non-draft and non-pending campaigns
  if (!['DRAFT', 'PENDING_DATA', 'PENDING_BILLING'].includes(campaign.status)) {
    return touchTriggers;
  }

  if (!isCampaignUsingCalendarBasedSystem(campaign.eventType.name)) {
    return touchTriggers;
  }

  // Touches are sorted by sendAt date when fetched, but need to sort it again for draft campaigns

  // First touch shown should always be the intro email touch
  const introEmailTouch = (campaign.touchTriggers as any[]).find((o) => o.description.includes('Email #0'));
  const introEmailTouchIndex = touchTriggers.indexOf(introEmailTouch);

  // We also have to calculate the estimated campaign start date and show the real touches on and after that date
  const { campaignStartDate } = calculateCampaignDates(campaign.eventType);

  const touchesWithoutIntroEmail = (campaign.touchTriggers as any[]).filter((o, i) => i !== introEmailTouchIndex);

  // For calendar-based campaigns, show the next outgoing touch at the top
  const campaignStartDateDOY = campaignStartDate.dayOfYear();

  // Set the year as the campaign start date's year before comparing the day of year. Edge
  // cases like leap years can mess this up!
  const firstTouch = touchesWithoutIntroEmail.reduce((previous: any, touchTrigger: any) => {
    const previousDOY = moment(previous.sendAt).year(campaignStartDate.year()).dayOfYear();
    const thisDOY = moment(touchTrigger.sendAt).year(campaignStartDate.year()).dayOfYear();

    if (thisDOY >= campaignStartDateDOY && thisDOY - campaignStartDateDOY < previousDOY - campaignStartDateDOY) return touchTrigger;

    return previous;
  }, touchesWithoutIntroEmail.find((touchTrigger: any) => moment(touchTrigger.sendAt).year(campaignStartDate.year()).dayOfYear() >= campaignStartDateDOY));

  const firstTouchIndex = touchesWithoutIntroEmail.indexOf(firstTouch);

  // Insert intro email, then touches on or after the campaign start date
  return [
    // Intro email touch may not exist for calendar-based campaign drafts created before this update
    ...introEmailTouch ? [introEmailTouch] : [],

    ...touchesWithoutIntroEmail.slice(firstTouchIndex),
    ...touchesWithoutIntroEmail.slice(0, firstTouchIndex),
  ];
};

// TODO: REMOVE???????
/* const getInitialVoicemailRecordings = (eventType, userInfo) => {
  let initialVoicemailRecordings;

  // Make sure there's an object in the array for each voicemail of this event type. The order of the voicemails array
  // should match the order of the voicemailTexts array in the event type!
  const eventVoicemails = userInfo.agentInfo.voicemailRecordings?.filter((o) => o.eventType === eventType._id);
  if (eventVoicemails && eventVoicemails.length > 0) {
    [initialVoicemailRecordings] = eventVoicemails;

    while (eventVoicemails[0].voicemails.length < eventType.voicemailTexts.length) {
      // We might add more voicemail texts to the event type, so make sure to initialize any
      // missing voicemail recordings as empty strings here
      eventVoicemails[0].voicemails.push('');
    }
  } else {
    initialVoicemailRecordings = {
      eventType: eventType._id,
      voicemails: eventType.voicemailTexts.map(() => ''), // Initialize with empty strings
    };
  }

  return initialVoicemailRecordings;
}; */

export const getHumanProcessingNotSentReason = (processingNotSentReason: ETouchTriggerNotSentReason): string => {
  switch (processingNotSentReason) {
    case ETouchTriggerNotSentReason.CampaignInactive:
      return 'Your campaign was inactive at the time this touch was scheduled to go out';
    case ETouchTriggerNotSentReason.MarketingProfileIncomplete:
      return 'Your marketing profile was incomplete at the time this touch was scheduled to go out';
    default:
      return '';
  }
};

const getIconForTouchType = (touchType: ETouchType) => {
  switch (touchType) {
    case ETouchType.Postcard:
      return ContactMailIcon;
    case ETouchType.Letter:
      return DescriptionIcon;
    case ETouchType.Email:
      return EmailIcon;
    case ETouchType.Text:
      return SmsIcon;
    case ETouchType.Voicemail:
      return VoicemailIcon;
    default:
      return null;
  }
};

const getColorForTouchType = (touchType: ETouchType) => {
  switch (touchType) {
    case ETouchType.Postcard:
      return '#F24E1D';
    case ETouchType.Letter:
      return '#A259FF';
    case ETouchType.Email:
      return '#09A366';
    case ETouchType.Text:
      return '#000000';
    case ETouchType.Voicemail:
      return '#018EDE';
    default:
      return null;
  }
};

const getEventDataOwnerInformation = (existingEventOverride: any, eventData: any) => {
  const { ownerContact } = eventData;

  let name;
  let phoneNumber;
  let email;
  let ownerAddress;
  if (existingEventOverride && existingEventOverride.newOwnerContact) {
    name = `${existingEventOverride.newOwnerContact.firstName || ''} ${existingEventOverride.newOwnerContact.lastName || ''}`; // || check here to prevent 'undefined' being concatenated
    phoneNumber = formatPhoneNumber(existingEventOverride.newOwnerContact.phoneNumber);
    email = existingEventOverride.newOwnerContact.email;
    ownerAddress = stringifyAddressObject(existingEventOverride.newOwnerContact.address);
  } else {
    name = `${ownerContact.firstName || ''} ${ownerContact.lastName || ''}`; // || check here to prevent 'undefined' being concatenated
    phoneNumber = formatPhoneNumber(ownerContact.phoneNumber); // Format these since they aren't formatted already when imported
    email = ownerContact.email;
    ownerAddress = stringifyAddressObject(ownerContact.address);
  }

  return {
    name,
    phoneNumber,
    email,
    ownerAddress,
    existingEventOverride,
    alternateEmails: ownerContact.alternateEmails,
    alternatePhoneNumbers: (ownerContact.alternatePhoneNumbers ?? []).map((s: string) => formatPhoneNumber(s)),
  };
};

const showAPIErrorAlert = (error: any) => {
  if (error.response && error.response.data.error) {
    toast.error(error.response.data.error, { duration: 6000 });
  } else {
    toast.error('Something went wrong, please try again.');
  }
};

// Check all required values, one from each section
const isMarketingProfileDirty = (agentInfo: any) => agentInfo && (agentInfo.agentPictureUrl || agentInfo.agentDreNumber || agentInfo.brokerageDreNumber);

// Check all required values, one from each section on the frontend. If one value in a section is written,
// then that means all required fields were written too.
// NOTE: Update on both frontend and backend if changes are made here!
const isMarketingProfileCompleted = (agentInfo: any) => agentInfo
  && agentInfo.agentPictureUrl
  && agentInfo.agentDreNumber
  && agentInfo.brokerageDreNumber;

const isMarketingSecondProfileCompleted = (agentInfo: any) => agentInfo
  && agentInfo.secondProfileFirstName
  && agentInfo.secondProfileLastName
  && agentInfo.secondProfileEmailAddress
  && agentInfo.secondProfilePhoneNumber
  && agentInfo.secondProfileWebsite
  && agentInfo.secondProfileLicenseNumber
  && agentInfo.secondProfilePictureUrl;

const openFileInNewWindow = (file: any) => {
  const newWindow = window.open(file, '_blank');

  // Check for blocked pop-ups
  if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
    toast.warning('It looks like our pop-up was blocked. Please allow pop-ups in your browser for Harvist.');
  }
};

const checkNextTouchIsVMWithUnassignedRecording = (touchTriggers: any[]) => {
  const nextTouchToGoOut = touchTriggers.reduce((nextTouch, current) => (!nextTouch && !current.processed ? current : nextTouch), null);
  return nextTouchToGoOut && nextTouchToGoOut.touchType.name === 'VOICEMAIL' && !nextTouchToGoOut.metadata.voicemail;
};

const getNumDaysAutoRenewalCampaignStartsIn = (createdAtDate: Date) => moment(createdAtDate).add(20, 'days').diff(moment(), 'days') + 1; // Add 1 to include current day

const transformObjectKeys = (obj: any, transformFn: (key: any)=> any) => {
  const transformedObject = {} as any;

  Object.keys(obj).forEach((key) => {
    transformedObject[transformFn(key)] = obj[key];
  });

  return transformedObject;
};

const tableFilterPhoneNumbers = (rows: any[], id: string, filterValue: string) => rows.filter((row) => {
  const stripFormatting = (phone: string) => phone.replace(/\D/g, '');

  const formattedPhoneNumbers = row.values[id];
  const rawPhoneNumbers = formattedPhoneNumbers.map((s: string) => stripFormatting(s));
  const filterValueStripped = stripFormatting(filterValue);

  return rawPhoneNumbers.find((s: string) => s.includes(filterValueStripped));
});

export {
  // getInitialVoicemailRecordings,
  intercomAppId, supportEmail, nameRegExp, nameRegExpGeneral, phoneRegExp, birthdayRegExp, birthdayNoYearRegExp, urlRegExp, httpsRegex, corporateOfficeLocation,
  compressImageUpload, stringifyAddressObject, stringifyEventSiteAddressObject, formatPhoneNumber,
  numberWithCommas, clampNumber, getIconForTouchType, getEventDataOwnerInformation,
  calculateCampaignDates, calculateCampaignPrices, supportPhoneNumber, supportPhoneNumberFormatted, numberAsAbbreviatedPrice,
  isABSEventType, isNODEventType, isCommercialEventType, isFullFarmEventType, isCrmProEventType, filterEventType, eventTypeNameToUrlPath,
  showAPIErrorAlert, populateCampaignEventDataEventOverrideData, getColorForTouchType, isCampaignDraft, isCampaignActiveOrCompleted, isCampaignPending, isCampaignCancelled,
  isMarketingProfileDirty, isMarketingProfileCompleted, isMarketingSecondProfileCompleted, openFileInNewWindow, checkNextTouchIsVMWithUnassignedRecording, isCampaignPaused, getNumDaysAutoRenewalCampaignStartsIn,
  transformObjectKeys, tableFilterPhoneNumbers, campaignEventTypeCanBeBilledAnnually,
};
